import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter, ViewEncapsulation, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, ValidationErrors, ControlValueAccessor, Validator, AbstractControl, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { MatDatepicker } from '@angular/material';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { filter, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { DateUtils, MY_DATE_FORMATS } from '@awp/core/utils';

export interface TravelDates {
  departureDate: Date;
  returnDate: Date;
}

export interface TravelOptions {
  minLeadTime: number;
  maxLeadTime: number;
  leadTimeType: 'DAY' | 'MONTH'
}

export interface DurationOptions {
  minDuration: number;
  maxDuration: number;
  durationType: 'DAY' | 'MONTH' | 'YEAR'
}

@Component({
  selector: 'ap-travel-dates',
  templateUrl: './travel-dates.component.html',
  host: {
    'class': 'ap-travel-dates',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TravelDatesComponent,
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: TravelDatesComponent,
      multi: true
    }
  ]
})
export class TravelDatesComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {

  form: FormGroup;
  dateFormats = MY_DATE_FORMATS;

  @Input() departureLabel: string;
  @Input() departureDate: Date;
  @Input() departureMinDate: Date;
  @Input() departureMaxDate: Date;
  @Input() departureRequired: boolean = true;
  @Input() departureOptions: TravelOptions;

  @Input() returnLabel: string;
  @Input() returnDate: Date;
  @Input() returnMinDate: Date;
  @Input() returnMaxDate: Date;
  @Input() returnRequired: boolean = true;
  @Input() returnDateVisible: boolean = true;
  @Input() durationOptions: DurationOptions;
  @Input() departurePlaceHolder: string;
  @Input() returnPlaceHolder: string;

  @Input() validationMessages: { [key: string]: string } = {};

  @ViewChild('returnPicker', { static: false }) returnPicker: MatDatepicker<any>;

  @Output() datesChanged = new EventEmitter<TravelDates>();


  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      departureDate: [this.departureDate, Validators.required],
      returnDate: [this.returnDate, Validators.required]
    },
    {
      validator: this.travelDatesDontCross
    });

    // Initialise defaults.. Input will override values
    this.setDepartureDateMinMax();
    this.setReturnDateMinMax();
  }

  ngOnInit() {
    this.form.get('departureDate').valueChanges
      .pipe(
        filter(p => p),
        untilDestroyed(this)
      )
      .subscribe(p => {
        this.setReturnDateMinMax(p, this.durationOptions);
      });

    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(p => {
        this.notifyChanged(p);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.departureRequired && changes.departureRequired.currentValue) {
      this.setDepartureDateValidators(changes.departureRequired.currentValue);
    }

    if (changes.returnRequired != null || changes.returnDateVisible != null) {
      this.setReturnDateValidators(this.returnDateVisible && this.returnRequired);
    }

    if (changes.departureDate && changes.departureDate.currentValue) {
      this.form.get('departureDate').setValue(changes.departureDate.currentValue);
    }

    if (changes.departureOptions && changes.departureOptions.currentValue) {
      this.setDepartureDateMinMax(changes.departureOptions.currentValue);
    }

    if (changes.returnDate && changes.returnDate.currentValue) {
      this.form.get('returnDate').setValue(changes.returnDate.currentValue);
    }

    if (changes.durationOptions != null && changes.durationOptions.currentValue != null) {
      this.setReturnDateMinMax(this.form.get('departureDate').value, this.durationOptions);
    }
  }

  ngOnDestroy(): void { }

  private notifyChanged(value: any): void {
    const travelDates = {
      departureDate: DateUtils.convertDate(value.departureDate),
      returnDate: DateUtils.convertDate(value.returnDate)
    }
    this.datesChanged.emit(travelDates)

    this._onChange(travelDates);
    this._onTouched();
  }

  private setDepartureDateValidators(isRequired: boolean): void {
    const depCtrl = this.form.get('departureDate');

    if (isRequired) {
      depCtrl.setValidators([Validators.required]);
      this._onValidatorChange();
    } else {
      depCtrl.clearValidators();
    }
    depCtrl.updateValueAndValidity();
  }

  private setReturnDateValidators(isRequired: boolean): void {
    const retCtrl = this.form.get('returnDate');
    if (isRequired) {
      retCtrl.setValidators([Validators.required]);
      this._onValidatorChange();
    } else {
      retCtrl.clearValidators();
    }
    retCtrl.updateValueAndValidity();
  }

  private setDepartureDateMinMax(options: TravelOptions | null = null): void {
    if (options == null) {
      this.departureMinDate = DateUtils.today();
      this.departureMaxDate = DateUtils.addYear(this.departureMinDate);
    } else if (options.leadTimeType === 'DAY') {
      this.departureMinDate = DateUtils.addDays(DateUtils.today(), options.minLeadTime);
      this.departureMaxDate = DateUtils.addDays(DateUtils.today(), options.maxLeadTime);
    } else if (options.leadTimeType === 'MONTH') {
      this.departureMinDate = DateUtils.addMonth(DateUtils.today(), options.minLeadTime);
      this.departureMaxDate = DateUtils.addMonth(DateUtils.today(), options.maxLeadTime);
    }
  }

  private setReturnDateMinMax(value: Date = null, options: DurationOptions = null): void {
    if (options == null) {
      this.returnMinDate = DateUtils.isDate(value) && value >= DateUtils.today() ? value : DateUtils.today();
      this.returnMaxDate = DateUtils.addYear(this.returnMinDate);
    } else {
      const departureDate = DateUtils.isDate(value) && value >= DateUtils.today() ? value : DateUtils.today();

      if (options.durationType === 'DAY') {
        this.returnMinDate = DateUtils.addDays(departureDate, options.minDuration > 0 ? options.minDuration - 1 : 0);
        this.returnMaxDate = DateUtils.addDays(departureDate, options.maxDuration - 1);
      } else if (options.durationType === 'MONTH') {
        this.returnMinDate = DateUtils.addMonth(departureDate, options.minDuration);
        this.returnMaxDate = DateUtils.addMonth(departureDate, options.maxDuration);
      } else if (options.durationType === 'YEAR') {
        this.returnMinDate = DateUtils.addYear(departureDate, options.minDuration);
        this.returnMaxDate = DateUtils.addMonth(departureDate, options.maxDuration);
      }
    }
  }

  //#region ControlValueAccessor

  _onTouched = () => { };
  _onChange = (value: TravelDates) => { };

  writeValue(value: TravelDates): void {
    if (value != null) {
      this.form.patchValue(value, { emitEvent: false });
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
    } else {
      this.form.reset();
    }

    this.setDepartureDateMinMax(this.departureOptions);
    if (value != null) {
      this.setReturnDateMinMax(value.departureDate, this.durationOptions);
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  //#endregion

  //#region Validator

  _onValidatorChange = () => { };

  validate(control: AbstractControl): ValidationErrors | null {
    return this.form.valid ? null : { invalid: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  //#endregion

  private travelDatesDontCross: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    const fromCtrl = control.get('departureDate');
    const toCtrl = control.get('returnDate');

    if (fromCtrl.value != null && toCtrl.value != null) {
      return fromCtrl.value > toCtrl.value ? { datesCross: true } : null;
    }
    return null;
  }

  //Departure date changed

  departureDateChangeEvent(): void {
    if (this.returnPicker != null) {
      this.returnPicker.open();
    }
  }
}
